import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';
import { useCartItems } from '../hooks/useCartItems';
import { useShippingQuote } from '../hooks/useShippingQuote';
import type { CartItem } from '../types/shipping';

interface Product {
  id: number;
  parentId?: number;
  name: string;
  price: string;
  shippingClass?: string;
  sku?: string;
}

interface ProductsResponse {
  products: Product[];
  language: string;
}

export default function CartIcon({ countryCode, stateCode }: { countryCode: string, stateCode: string }) {
  const [showSidebar, setShowSidebar] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items: cartItems, itemsCount, removeItem } = useCartItems();
  const [showBottleWarning, setShowBottleWarning] = useState(false);
  const [bottleWarningInfo, setBottleWarningInfo] = useState<{ bottleCount: number, neededToAdd: number }>({ bottleCount: 0, neededToAdd: 0 });

  // Carregar els productes per calcular el total
  const { data: productsResponse } = useQuery<ProductsResponse>({
    queryKey: ['products', countryCode, i18n.language],
    queryFn: async () => {
      if (!countryCode) return { products: [] };
      const response = await fetch(`/api/products/${countryCode}?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error(t('products.error'));
      }
      return response.json();
    }
  });

  // Consulta per saber si el país actual requereix comprar en múltiples de 6
  const { data: countryInfo } = useQuery<{ requiresSixBottleMultiples?: boolean }>({
    queryKey: ['countryRequiresSixBottleMultiples', countryCode],
    queryFn: async () => {
      if (!countryCode) return { requiresSixBottleMultiples: false };
      const response = await fetch('/api/countries', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Error obtenint informació del país');
      }
      const countries = await response.json();
      const country = countries.find((c: any) => c.code === countryCode);
      return country || {};
    }
  });

  // Obtenim l'array de productes de la resposta (memoitzat perquè no canviï
  // de referència a cada render i desestabilitzi els useMemo que en depenen)
  const products = useMemo(() => productsResponse?.products || [], [productsResponse]);

  // Font única de veritat pel càlcul d'enviament: es recalcula automàticament
  // en canviar el carret (el mini-carret no té camps de codi postal/ciutat
  // propis, així que el hook fa servir sempre els valors provisionals).
  const { methods } = useShippingQuote({
    countryCode,
    state: stateCode,
    postcode: '',
    city: '',
    items: cartItems,
    products,
    enabled: !!countryCode && !!stateCode && cartItems.length > 0
  });

  const shippingMethod = methods[0] ?? null;

  // Subtotal, cost d'enviament i total es deriven sempre de l'estat actual
  // (carret + productes + mètode d'enviament): en buidar-se el carret o
  // desaparèixer els mètodes tornen a 0 automàticament, sense necessitat
  // d'un useEffect que "recordi" un valor anterior.
  const cartSubtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      if (!product) return sum;
      const price = parseFloat(product.price);
      return isNaN(price) ? sum : sum + price * item.quantity;
    }, 0);
  }, [cartItems, products]);

  const shippingCost = useMemo(() => {
    if (!shippingMethod) return 0;
    const cost = parseFloat(String(shippingMethod.cost).replace(',', '.'));
    return isNaN(cost) ? 0 : cost;
  }, [shippingMethod]);

  const cartTotal = useMemo(() => cartSubtotal + shippingCost, [cartSubtotal, shippingCost]);

  // Funció per validar els múltiples de 6 ampolles
  const validateBottleMultiples = useCallback((products: Product[], cartItems: CartItem[]) => {
    if (!countryInfo?.requiresSixBottleMultiples) {
      return;
    }

    // Contar ampolles estàndard i magnum
    let totalBottleCount = 0;
    
    // Mostrar tota la informació dels productes al carret per depuració
    console.log("[MiniCart] ====== DEBUGGING BOTTLE MULTIPLES ======");
    
    cartItems.forEach(item => {
      const product = products.find(p => p.id === item.productId);
      if (product) {
        // Obtenir la shipping class del producte
        const shippingClass = product.shippingClass || '';
        
        // Debug: Imprimir la shipping class per identificar els valors reals
        console.log(`[MiniCart] Producte ${product.id} (${product.name}): shipping class = "${shippingClass}"`);
        
        // SOLUCIÓ 1: Comprovem si és ampolla magnum utilitzant múltiples criteris
        const decodedName = decodeHtmlEntities(product.name).toLowerCase();
        if (
          shippingClass.toLowerCase().includes('magnum') || 
          decodedName.includes('magnum') ||
          decodedName.includes('150cl') ||
          product.sku?.toLowerCase().includes('magnum')
        ) {
          console.log(`[MiniCart]   → Ampolla MAGNUM detectada (${item.quantity} x 2 = ${item.quantity * 2} ampolles)`);
          totalBottleCount += item.quantity * 2;
        } 
        // SOLUCIÓ 2: Si és ampolla estàndard (més criteris de detecció)
        else if (
          shippingClass.toLowerCase().includes('std-ca') || 
          shippingClass.toLowerCase().includes('ampolla') ||
          shippingClass.toLowerCase().includes('botella') ||
          shippingClass.toLowerCase().includes('std') ||
          // Si no té shipping class però el nom conté indicadors d'ampolla estàndard
          (!shippingClass && (
            decodedName.includes('75cl') ||
            decodedName.includes('0,75l') ||
            decodedName.includes('0.75l'))
          )
        ) {
          console.log(`[MiniCart]   → Ampolla estàndard detectada (${item.quantity} ampolles)`);
          totalBottleCount += item.quantity;
        }
        // Si no coincideix amb cap dels patrons anteriors, intentar detectar per nom/sku
        else {
          // Darrer intent: si el nom o SKU conté "vi" o "vino" o "wine", és probablement una ampolla estàndard
          if (
            decodedName.includes('vi ') || 
            decodedName.includes('vino') || 
            decodedName.includes('wine') ||
            product.sku?.toLowerCase().includes('vi')
          ) {
            console.log(`[MiniCart]   → Ampolla estàndard detectada per nom/SKU (${item.quantity} ampolles)`);
            totalBottleCount += item.quantity;
          } else {
            console.log(`[MiniCart]   → No és una ampolla, no es compta`);
          }
        }
      }
    });
    
    console.log(`[MiniCart] Total d'ampolles computades: ${totalBottleCount}`);
    console.log("[MiniCart] ====== FI DEBUGGING BOTTLE MULTIPLES ======");
    
    // Validar que el número total d'ampolles és múltiple de 6
    const isValid = totalBottleCount % 6 === 0 && totalBottleCount > 0;
    
    if (!isValid) {
      const remainder = totalBottleCount % 6;
      const neededToAdd = remainder === 0 ? 6 : 6 - remainder;
      
      setShowBottleWarning(true);
      setBottleWarningInfo({
        bottleCount: totalBottleCount,
        neededToAdd: neededToAdd
      });
    } else {
      setShowBottleWarning(false);
    }
  }, [countryInfo]);

  // Comprovar requisits de múltiples de 6 ampolles cada vegada que canvia el carret
  useEffect(() => {
    if (countryInfo?.requiresSixBottleMultiples && products.length > 0 && cartItems.length > 0) {
      validateBottleMultiples(products, cartItems);
    } else {
      setShowBottleWarning(false);
    }
  }, [products, cartItems, countryInfo, validateBottleMultiples]);

  const handleCartClick = () => {
    setShowSidebar(false);
    navigate('/cart');
  };

  const handleRemoveItem = (productId: number) => {
    removeItem(productId);
  };

  const isCartPage = window.location.pathname === '/cart';

  return (
    <div className="relative">
      <button 
        className="flex items-center justify-center bg-white hover:bg-gray-100 text-black p-2 rounded-md h-9 border-2 border-black"
        onClick={() => !isCartPage && setShowSidebar(true)}
        aria-label={t('cart.cart')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <span className="ml-1">{`(${itemsCount})`}</span>
      </button>

      {/* Overlay fosc */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          showSidebar ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setShowSidebar(false)}
      />

      {/* Barra lateral del carret */}
      <div 
        className={`fixed right-0 top-0 h-full w-[80%] lg:w-[35%] bg-white z-50 transform transition-all duration-300 ease-out ${
          showSidebar ? 'translate-x-0 shadow-2xl' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Capçalera */}
          <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">{t('cart.summary')}</h2>
            <button 
              onClick={() => setShowSidebar(false)}
              className="text-black"
              aria-label={t('common.close')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Contingut del carret */}
          <div className="flex-1 overflow-y-auto p-6">
            {cartItems.length === 0 ? (
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <p className="text-gray-500">{t('cart.emptyCart')}</p>
                <button 
                  onClick={() => setShowSidebar(false)}
                  className="mt-4 text-sm bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                >
                  {t('cart.returnToShop')}
                </button>
              </div>
            ) : (
              <>
                {/* Advertència de múltiples de 6 ampolles */}
                {showBottleWarning && (
                  <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-2 mb-3 text-xs">
                    <p>{t('cart.sixBottleWarning', { 
                      bottleCount: bottleWarningInfo.bottleCount, 
                      neededToAdd: bottleWarningInfo.neededToAdd 
                    })}</p>
                  </div>
                )}

                {/* Llista d'items */}
                <div className="space-y-4">
                  {cartItems.map((item) => {
                    const product = products.find(p => p.id === item.productId);
                    if (!product) return null;
                    
                    return (
                      <div key={item.productId} className="flex justify-between items-center group">
                        <span className="text-sm flex-grow">
                          {item.quantity}x {decodeHtmlEntities(product.name)}
                        </span>
                        <span className="text-sm font-medium mx-4">
                          {(parseFloat(product.price) * item.quantity).toFixed(2)}€
                        </span>
                        <button
                          onClick={() => handleRemoveItem(item.productId)}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                          aria-label={t('cart.removeItem')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Línia separadora */}
                <div className="h-px bg-gray-200 my-4" />

                {/* Subtotal i enviament */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{t('cart.subtotal')}:</span>
                    <span className="font-medium">{cartSubtotal.toFixed(2)}€</span>
                  </div>
                  
                  {shippingMethod && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {t('cart.shipping')} ({decodeHtmlEntities(shippingMethod.title)}):
                      </span>
                      <span className="font-medium">{shippingCost.toFixed(2)}€</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="font-bold">{t('cart.total')}:</span>
                    <span className="font-bold">{cartTotal.toFixed(2)}€</span>
                  </div>

                </div>

                  {/* Línia separadora */}
                  <div className="h-px bg-gray-200 my-4" />
                  
                  {/* Botó de checkout */}
                  <button 
                    onClick={handleCartClick}
                    className="w-full py-2 bg-black hover:bg-gray-900 text-white rounded text-sm"
                  >
                    {t('cart.checkout')}
                  </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}